/**
 * Meal plan UI helpers — maps API shapes to planner components (WeekPlanner / MobilePlanner).
 * day_of_week from API: 0 = Monday … 6 = Sunday (ISO week, aligned with backend week_start Monday).
 */

import { formatTotalTimeLabel, getSavedListTotalMinutes } from './recipeMetadataTime.js'
import { getRecipeCookReadiness } from './recipeCookReadiness.js'
import { isNightOffMeal, nightOffLabel, planItemIsAssigned } from './nightOff.js'

export const MEAL_PLAN_DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

/** Long labels for MON–SUN keys (calendar strip + meal rows). */
export const PLANNER_DAY_LONG = Object.freeze({
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun',
})

/** Single-letter day abbreviations for the minimized calendar strip. */
export const PLANNER_DAY_LETTER = Object.freeze({
  MON: 'M',
  TUE: 'T',
  WED: 'W',
  THU: 'T',
  FRI: 'F',
  SAT: 'S',
  SUN: 'S',
})

/** Skip list for calendar strip meal cue words (not useful as identity). */
const CALENDAR_MEAL_LABEL_SKIP = new Set([
  'a', 'an', 'the', 'and', 'or', 'with', 'of', 'for', 'in', 'on', 'to', 'from', 'over', 'under',
  'my', 'our', 'your', 'his', 'her', 'their',
  'easy', 'quick', 'best', 'classic', 'homemade', 'simple', 'style', 'recipe', 'famous', 'ultimate',
  'perfect', 'authentic', 'traditional', 'healthy', 'lighter', 'loaded', 'crispy', 'creamy', 'spicy',
  'roasted', 'baked', 'grilled', 'fried', 'sauteed', 'sautéed', 'braised', 'poached', 'steamed',
  'sheet', 'pan', 'one', 'pot', 'skillet', 'instant', 'air', 'fryer', 'slow', 'cooker', 'pressure',
  'weeknight', 'weekend', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  'dinner', 'supper', 'lunch', 'breakfast', 'meal', 'dish',
  'nonna', 'grandma', 'grandfather', 'nana', 'mama', 'mom', 'dad', 'family',
])

/** Prefer these when present — strongest week-to-week identity. */
const CALENDAR_MEAL_PROTEINS = new Set([
  'chicken', 'beef', 'pork', 'lamb', 'turkey', 'duck', 'veal',
  'steak', 'steaks',
  'salmon', 'tuna', 'cod', 'halibut', 'shrimp', 'prawns', 'scallops', 'crab', 'lobster', 'fish',
  'tofu', 'tempeh', 'seitan',
])

/** Dish / format nouns — used when no protein token is present. */
const CALENDAR_MEAL_DISHES = new Set([
  'tacos', 'taco', 'burritos', 'burrito', 'enchiladas', 'fajitas', 'quesadillas',
  'pasta', 'spaghetti', 'linguine', 'fettuccine', 'penne', 'lasagna', 'lasagne', 'ravioli', 'gnocchi', 'noodles',
  'risotto', 'paella', 'pilaf',
  'soup', 'stew', 'chili', 'chowder', 'curry', 'stirfry', 'stir-fry',
  'pizza', 'flatbread', 'burger', 'burgers', 'sandwich', 'sandwiches', 'wrap', 'wraps',
  'salad', 'slaw', 'bowl', 'bowls',
  'meatballs', 'meatloaf', 'kebab', 'kebabs', 'wings', 'ribs', 'drumstick', 'drumsticks',
  'ramen', 'pho', 'udon', 'dumpling', 'dumplings', 'potstickers',
  'pancake', 'pancakes', 'waffle', 'waffles', 'frittata', 'omelette', 'omelet', 'quiche',
  'casserole', 'bake', 'pie', 'galette', 'tart',
])

/** Hyphenated prep adjectives (e.g. sumac-rubbed) — never useful as identity. */
const CALENDAR_MEAL_HYPHEN_PREP_SUFFIXES = new Set([
  'rubbed', 'crusted', 'glazed', 'coated', 'tossed', 'seared', 'smoked',
  'brined', 'marinated', 'stuffed', 'topped', 'breaded', 'battered',
  'poached', 'braised', 'roasted', 'grilled', 'baked', 'fried',
])

/**
 * @param {string} key
 * @returns {boolean}
 */
function isHyphenatedPrepModifier(key) {
  if (!key.includes('-')) return false
  const last = key.slice(key.lastIndexOf('-') + 1)
  return CALENDAR_MEAL_HYPHEN_PREP_SUFFIXES.has(last)
}

/**
 * @param {string} token
 * @returns {string} lowercase alphanumeric key (keeps hyphen for stir-fry)
 */
function normalizeCalendarLabelToken(token) {
  return String(token || '')
    .toLowerCase()
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, '')
    .replace(/[''']s$/i, '') // Grandma's → grandma
    .replace(/[^a-z0-9-]/gi, '')
}

/**
 * @param {string} cleaned
 * @returns {string}
 */
function titleCaseCalendarLabel(cleaned) {
  if (!cleaned) return ''
  // Preserve internal hyphens: stir-fry → Stir-fry
  return cleaned
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part))
    .join('-')
}

/**
 * Short cue word for calendar-strip assigned days.
 * Resolution: stored short_label → protein token → dish token → first content word.
 * @param {string|null|undefined} name
 * @param {{ shortLabel?: string|null }|string|null} [opts] — optional stored label, or legacy 2nd-arg string
 * @returns {string}
 */
export function calendarMealShortLabel(name, opts = null) {
  const shortLabel =
    typeof opts === 'string'
      ? opts
      : (opts && typeof opts === 'object' ? opts.shortLabel : null)
  if (shortLabel && typeof shortLabel === 'string' && shortLabel.trim()) {
    const stored = shortLabel.trim().replace(/\s+/g, ' ')
    // Cap stored labels to one scannable phrase (max ~2 words / 14 chars display via CSS)
    const firstChunk = stored.split(/[\s,/]+/)[0]
    return titleCaseCalendarLabel(normalizeCalendarLabelToken(firstChunk) || firstChunk)
  }

  if (!name || typeof name !== 'string') return ''

  // Join common multi-word cooking compounds before scoring
  const prepared = name
    .trim()
    .replace(/\bstir[\s-]+fry\b/gi, 'stir-fry')
    .replace(/\bsheet[\s-]+pan\b/gi, 'sheet-pan')
    .replace(/\bone[\s-]+pot\b/gi, 'one-pot')

  const tokens = prepared.split(/[\s,/|&–—]+/).filter(Boolean)
  let protein = null
  let dish = null
  let content = null

  for (const raw of tokens) {
    const key = normalizeCalendarLabelToken(raw)
    if (!key || key.length < 2) continue
    if (CALENDAR_MEAL_LABEL_SKIP.has(key)) continue
    if (key === 'sheet-pan' || key === 'one-pot') continue
    if (isHyphenatedPrepModifier(key)) continue

    if (!protein && CALENDAR_MEAL_PROTEINS.has(key)) {
      protein = key
      continue
    }
    if (!dish && CALENDAR_MEAL_DISHES.has(key)) {
      dish = key
      continue
    }
    if (!content && key.length >= 3 && !CALENDAR_MEAL_LABEL_SKIP.has(key)) {
      content = key
    }
  }

  // Prefer protein for week variety; dish when it's the identity ("Tacos", "Risotto")
  // Exception: if dish is a strong format and protein exists, still prefer protein
  // ("Chicken Tacos" → Chicken). Pure dish titles keep the dish ("Spaghetti Carbonara" → Spaghetti).
  const picked = protein || dish || content
  if (!picked) {
    const fallback = tokens[0] ? normalizeCalendarLabelToken(tokens[0]) : ''
    return titleCaseCalendarLabel(fallback)
  }
  return titleCaseCalendarLabel(picked)
}

/**
 * Library / pantry thresholds for planner state + UI hints (aligned with backend product rules).
 * cold-start: library <= coldStartLibrary; thin-library: library < thinLibrary; empty-pantry: pantry < emptyPantry; else healthy.
 */
export const THRESHOLDS = Object.freeze({
  coldStartLibrary: 2,
  thinLibrary: 8,
  emptyPantry: 6,
  repeatDays: 14,
})

/**
 * @param {{ libraryCount: number, pantryCount: number }} p
 * @returns {'healthy' | 'thin-library' | 'empty-pantry' | 'cold-start'}
 */
export function derivePlannerState({ libraryCount, pantryCount }) {
  if (libraryCount <= THRESHOLDS.coldStartLibrary) return 'cold-start'
  if (pantryCount < THRESHOLDS.emptyPantry) return 'empty-pantry'
  if (libraryCount < THRESHOLDS.thinLibrary) return 'thin-library'
  return 'healthy'
}

/** Cold-start empty state when the user has almost no saved recipes (live planner only). */
export const COLD_START_PLANNER_HERO = Object.freeze({
  eyebrow: 'Almost ready to plan',
  title: 'Add more saved recipes so Captain Cook can plan a varied week for you.',
  body: 'Import meals you already cook from Recipes, then add them to open nights.',
})

const MS_PER_DAY = 86400000

/**
 * Live planner banners for thin-library and empty-pantry.
 * @param {object} p
 * @param {string} p.plannerState — derivePlannerState result
 * @param {number|null} p.libraryCount
 * @param {number|null} p.pantryCount
 * @param {object[]} p.assignedMeals — live assigned meal cards (thin-library branch only)
 * @param {object[]} p.recipeLists — saved-list summaries (thin-library branch only)
 * @returns {{ tone: string, eyebrow: string, title: string|null, body: string, bodyStrong?: string, dismissible?: boolean } | null}
 */
export function deriveLivePlannerBanner({
  plannerState,
  libraryCount,
  pantryCount,
  assignedMeals,
  recipeLists,
}) {
  if (plannerState !== 'thin-library' && plannerState !== 'empty-pantry') return null
  if (libraryCount == null || pantryCount == null) return null

  if (plannerState === 'thin-library') {
    const assigned = (assignedMeals || []).filter((m) => m && m.recipeId)
    const byRecipeId = Object.fromEntries(
      (recipeLists || []).filter((r) => r && r.id != null).map((r) => [String(r.id), r]),
    )
    const repeatDays = THRESHOLDS.repeatDays
    const n = assigned.length
    let recent = 0
    for (const m of assigned) {
      const r = byRecipeId[m.recipeId]
      const raw = r?.last_cooked_date
      if (!raw) continue
      const t = new Date(raw).getTime()
      if (!Number.isFinite(t)) continue
      if (Date.now() - t <= repeatDays * MS_PER_DAY) recent += 1
    }
    const needMore = Math.max(0, THRESHOLDS.thinLibrary - libraryCount)
    const libLabel = `${libraryCount} saved recipe${libraryCount === 1 ? '' : 's'}`
    const repeatLabel = `${recent} of ${n} planned dinner${n === 1 ? '' : 's'}`
    const repeatPhrase = `${repeatLabel} ${recent === 1 ? 'is' : 'are'} a repeat`

    if (n > 0 && recent > 0) {
      return {
        tone: 'soft',
        eyebrow: 'Light week',
        title: 'A few recipes, familiar picks',
        body: needMore > 0
          ? `${repeatPhrase}. Add about ${needMore} recipes so suggestions rotate more easily.`
          : `${repeatPhrase}. Recipe packs still add variety without a huge shop list.`,
        bodyStrong: repeatLabel,
      }
    }

    return {
      tone: 'soft',
      eyebrow: 'Light week',
      title: 'Add more recipes',
      body: needMore > 0
        ? `You have ${libLabel}. Add about ${needMore} recipes so the planner has more to choose from.`
        : `You have ${libLabel}. Import meals you already cook or browse packs for variety.`,
      bodyStrong: libLabel,
    }
  }

  // empty-pantry (pantry count 0–5); copy is static for 0 items — no recipe / assignment counts.
  if (pantryCount === 0) {
    return {
      tone: 'info',
      eyebrow: 'Light pantry',
      title: null,
      dismissible: true,
      body: 'You have 0 pantry items. Add missing items to Shop when you are ready.',
      bodyStrong: '0 pantry items',
    }
  }

  return {
    tone: 'info',
    eyebrow: 'Light pantry',
    title: 'Stock up as you plan',
    dismissible: true,
    body: `You have ${pantryCount} pantry items. Add missing items to Shop when you are ready.`,
    bodyStrong: `${pantryCount} pantry items`,
  }
}

/** @param {number} dow 0–6 */
export function dayKeyFromDow(dow) {
  return MEAL_PLAN_DAY_ORDER[dow] ?? 'MON'
}

/** @param {string} key MON–SUN */
export function dowFromDayKey(key) {
  const i = MEAL_PLAN_DAY_ORDER.indexOf(key)
  return i === -1 ? 0 : i
}

/** Parse YYYY-MM-DD as local calendar date (no UTC shift). */
export function parseLocalDateOnly(iso) {
  if (!iso || typeof iso !== 'string') return new Date()
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Monday of the week containing `date` (local). */
export function mondayOfWeekContaining(date) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const day = d.getDay()
  const diff = (day + 6) % 7
  d.setDate(d.getDate() - diff)
  return d
}

/** Monday (YYYY-MM-DD) of the user's current local calendar week. */
export function currentWeekStartIso() {
  return formatLocalDateIso(mondayOfWeekContaining(new Date()))
}

/** How many weeks ahead of the current week the planner may navigate (0 = only current). */
export const PLANNER_MAX_FUTURE_WEEKS = 2

/**
 * Shift a plan-week Monday by `deltaWeeks` (negative = earlier).
 * @param {string} weekStartIso — Monday YYYY-MM-DD
 * @param {number} deltaWeeks
 */
export function addWeeksToMonday(weekStartIso, deltaWeeks) {
  const monday = parseLocalDateOnly(weekStartIso)
  monday.setDate(monday.getDate() + deltaWeeks * 7)
  return formatLocalDateIso(monday)
}

/** @param {string} weekStartIso */
export function isCurrentWeek(weekStartIso) {
  return weekStartIso === currentWeekStartIso()
}

/** True when the plan week is strictly after the current calendar week. */
export function isFutureWeek(weekStartIso) {
  return weekStartIso > currentWeekStartIso()
}

/** Latest Monday the planner may show (current + PLANNER_MAX_FUTURE_WEEKS). */
export function maxPlannerViewWeekStartIso() {
  return addWeeksToMonday(currentWeekStartIso(), PLANNER_MAX_FUTURE_WEEKS)
}

/**
 * Hero title for Week / Mobile planner. Kept stable across week navigation so
 * the week picker does not wrap/shift on mobile when the title length changes.
 * @param {string} _viewedWeekStart — Monday YYYY-MM-DD (unused; kept for call-site stability)
 * @param {boolean} isCold
 */
export function plannerHeroTitleForWeek(_viewedWeekStart, isCold) {
  if (isCold) return 'Almost ready to plan'
  return 'Plan Week'
}

/**
 * @param {string} weekStartIso — Monday YYYY-MM-DD
 * @returns {Record<string, number>} day key → date of month for strip labels
 */
export function buildDayDates(weekStartIso) {
  const monday = parseLocalDateOnly(weekStartIso)
  const out = {}
  MEAL_PLAN_DAY_ORDER.forEach((key, i) => {
    const dt = new Date(monday)
    dt.setDate(monday.getDate() + i)
    out[key] = dt.getDate()
  })
  return out
}

/**
 * Which MON–SUN key is "today" if today falls in this plan week; else null.
 * @param {string} weekStartIso
 */
export function todayKeyInPlanWeek(weekStartIso) {
  const monday = parseLocalDateOnly(weekStartIso)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const t = localTodayDate()
  if (t < monday || t > sunday) return null
  const delta = Math.round((t - monday) / MS_PER_DAY)
  return MEAL_PLAN_DAY_ORDER[delta] ?? null
}

/** Start of today in the user's local calendar (midnight). */
export function localTodayDate() {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/** Format a local Date as YYYY-MM-DD. */
export function formatLocalDateIso(d) {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/** Normalize API date to YYYY-MM-DD. */
export function normalizeSlotDateIso(value) {
  if (value == null || value === '') return null
  if (typeof value === 'string') return value.split('T')[0]
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear()
    const mo = String(value.getMonth() + 1).padStart(2, '0')
    const d = String(value.getDate()).padStart(2, '0')
    return `${y}-${mo}-${d}`
  }
  return String(value).split('T')[0]
}

/** Slot calendar date for a MON–SUN key within the plan week (local). */
export function slotDateForDayKey(weekStartIso, dayKey) {
  const monday = parseLocalDateOnly(weekStartIso)
  const offset = dowFromDayKey(dayKey)
  const dt = new Date(monday)
  dt.setDate(monday.getDate() + offset)
  return dt
}

/** True when the slot's calendar day is strictly before local today. */
export function isPastDayKey(weekStartIso, dayKey) {
  return slotDateForDayKey(weekStartIso, dayKey) < localTodayDate()
}

/** True when a slot ISO date is strictly before local today. */
export function isPastSlotDate(slotDateIso) {
  const iso = normalizeSlotDateIso(slotDateIso)
  if (!iso) return false
  return parseLocalDateOnly(iso) < localTodayDate()
}

/** Local midnight for a date-only string or timestamp (account/plan created_at). */
export function localCalendarDateFromTimestamp(value) {
  if (!value) return null
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const raw = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = parseLocalDateOnly(raw)
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null
  }
  const dt = new Date(raw)
  if (Number.isNaN(dt.getTime())) return null
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
}

/**
 * When the person first had a kitchen this week: account creation, else first
 * guest/onboarding generate. Returning accounts keep existing past-night prompts.
 */
export function resolveKitchenStartedAt({
  accountCreatedAt = null,
  planGeneratedAt = null,
  guestMode = false,
  guestTrial = false,
} = {}) {
  if (accountCreatedAt) return accountCreatedAt
  if (guestMode || guestTrial) {
    return planGeneratedAt || formatLocalDateIso(localTodayDate())
  }
  return null
}

/**
 * Skip / night-off / “Did you cook this?” only if they already had the kitchen
 * that calendar day. Days before onboarding or account creation stay quiet.
 */
export function shouldPromptPastNightResolve({
  slotDateIso = null,
  weekStartIso = null,
  dayKey = null,
  kitchenStartedAt = null,
} = {}) {
  if (!kitchenStartedAt) return true
  const slotIso =
    normalizeSlotDateIso(slotDateIso) ||
    (weekStartIso && dayKey
      ? formatLocalDateIso(slotDateForDayKey(weekStartIso, dayKey))
      : null)
  if (!slotIso) return true
  const slot = parseLocalDateOnly(slotIso)
  const started = localCalendarDateFromTimestamp(kitchenStartedAt)
  if (!slot || !started || Number.isNaN(slot.getTime()) || Number.isNaN(started.getTime())) {
    return true
  }
  return slot >= started
}

/**
 * MON–SUN keys that are in the past for this plan week (browser-local today).
 * Entire past/future weeks return all keys or none.
 */
/**
 * Onboarding dinners start tomorrow. Past nights and today stay in the week
 * strip; the row list is only the nights still ahead.
 */
export function isRestOfWeekDinnerDay(dayKey, todayKey) {
  if (!todayKey) return true
  const todayIdx = MEAL_PLAN_DAY_ORDER.indexOf(todayKey)
  const idx = MEAL_PLAN_DAY_ORDER.indexOf(dayKey)
  if (todayIdx < 0 || idx < 0) return true
  return idx > todayIdx
}

/**
 * Nights after today through Sunday. Friday is 2, Saturday is 1, Sunday is 0.
 * @param {Date} [today]
 */
export function nightsLeftAfterToday(today = localTodayDate()) {
  const day = today.getDay()
  if (day === 0) return 0
  return 7 - day
}

/**
 * Friday, Saturday, and Sunday don't leave enough nights to plan a week.
 * Setup opens next week so two dinners — and the save prompt — can land.
 * @param {Date} [today]
 */
export function onboardingShouldOpenNextWeek(today = localTodayDate()) {
  return nightsLeftAfterToday(today) <= 2
}

/** Monday the setup plan should open, given how many nights are left. */
export function onboardingPlanWeekStartIso(today = localTodayDate()) {
  const current = formatLocalDateIso(mondayOfWeekContaining(today))
  if (!onboardingShouldOpenNextWeek(today)) return current
  return addWeeksToMonday(current, 1)
}

/** Lead under the Dinners title during setup. */
export function onboardingPlanLead(today = localTodayDate()) {
  if (onboardingShouldOpenNextWeek(today)) {
    return 'Next week, so you can plan dinners across the week.'
  }
  return 'The rest of this week, starting tomorrow.'
}

/** Setup lets someone place two dinners, then asks them to save before another. */
export const ONBOARDING_SAVE_AFTER_DINNERS = 2

export function countPlannedDinners(meals) {
  return (meals || []).filter((meal) => meal?.recipeId).length
}

export function onboardingPlanningIsGated(dinnersOnWeek, saveAskDismissed) {
  if (saveAskDismissed) return false
  return dinnersOnWeek >= ONBOARDING_SAVE_AFTER_DINNERS
}

export function onboardingShouldAskToSave(dinnersAfter, saveAskDismissed) {
  if (saveAskDismissed) return false
  return dinnersAfter >= ONBOARDING_SAVE_AFTER_DINNERS
}

export function pastDayKeysInPlanWeek(weekStartIso) {
  const monday = parseLocalDateOnly(weekStartIso)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const today = localTodayDate()
  if (sunday < today) return [...MEAL_PLAN_DAY_ORDER]
  if (today < monday) return []
  return MEAL_PLAN_DAY_ORDER.filter((key) => isPastDayKey(weekStartIso, key))
}

/**
 * First today-or-future plan slot with no recipe assigned (Mon → Sun order).
 * @param {{ week_start_date?: string, items?: Array<{ id: string, recipe_id?: string|null, day_of_week: number, date?: string }> }} plan
 * @returns {NonNullable<typeof plan.items>[number] | null}
 */
export function findNextOpenPlanItem(plan) {
  const items = Array.isArray(plan?.items) ? plan.items : []
  const weekStart = plan?.week_start_date
  const sorted = [...items].sort((a, b) => (a.day_of_week ?? 0) - (b.day_of_week ?? 0))
  for (const item of sorted) {
    if (planItemIsAssigned(item)) continue
    const dayKey = dayKeyFromDow(item.day_of_week)
    const slotPast = weekStart
      ? isPastDayKey(weekStart, dayKey)
      : isPastSlotDate(item.date)
    if (slotPast) continue
    return item
  }
  return null
}

/**
 * Calendar context for the active plan week: strip labels, today highlight, past-day locks.
 * @param {string} weekStartIso — Monday YYYY-MM-DD
 */
export function buildPlannerCalendarContext(weekStartIso) {
  const pastDayKeys = pastDayKeysInPlanWeek(weekStartIso)
  const pastSet = new Set(pastDayKeys)
  return {
    dayDates: buildDayDates(weekStartIso),
    todayKey: todayKeyInPlanWeek(weekStartIso),
    pastDayKeys,
    isPastDay: (dayKey) => pastSet.has(dayKey),
  }
}

/**
 * Build seven strip-day descriptors for one derived meal plan week.
 * Used by the calendar strip (including sliding windows across weeks).
 * @param {object|null|undefined} derived — from deriveLivePlannerState
 * @returns {Array<{
 *   key: string,
 *   dayKey: string,
 *   weekStartDate: string,
 *   planId: string,
 *   planItemId: string|null,
 *   dateNum: number,
 *   dateIso: string,
 *   isPast: boolean,
 *   isToday: boolean,
 *   status: 'planned' | 'cooked' | 'skipped' | 'night_off',
 *   meal: object|null,
 * }>}
 */
export function buildPlannerStripDays(derived) {
  if (!derived?.weekStartDate || !derived?.planId) return []
  const weekStart = derived.weekStartDate
  const cal = buildPlannerCalendarContext(weekStart)
  const assignedByDay = Object.fromEntries(
    (derived.assigned || []).map((m) => [m.assignedTo, m]),
  )
  return MEAL_PLAN_DAY_ORDER.map((dayKey) => {
    const slot = derived.slotsByDay?.[dayKey] || null
    const dateIso =
      normalizeSlotDateIso(slot?.slotDate) ||
      formatLocalDateIso(slotDateForDayKey(weekStart, dayKey))
    const rawStatus = typeof slot?.status === 'string' ? slot.status : 'planned'
    const status =
      rawStatus === 'cooked' || rawStatus === 'skipped' || rawStatus === 'night_off'
        ? rawStatus
        : 'planned'
    return {
      key: `${weekStart}:${dayKey}`,
      dayKey,
      weekStartDate: weekStart,
      planId: String(derived.planId),
      planItemId: slot?.planItemId ? String(slot.planItemId) : null,
      dateNum: cal.dayDates[dayKey],
      dateIso,
      isPast: Boolean(slot?.isPast) || cal.isPastDay(dayKey),
      isToday: cal.todayKey != null && cal.todayKey === dayKey,
      status,
      meal: assignedByDay[dayKey] || null,
    }
  })
}

/**
 * Concatenate this week + optional next week for drag edge-advance.
 * @param {object|null|undefined} primaryDerived
 * @param {object|null|undefined} nextDerived
 */
export function buildExtendedPlannerStripDays(primaryDerived, nextDerived = null) {
  const primary = buildPlannerStripDays(primaryDerived)
  if (!nextDerived?.weekStartDate || !nextDerived?.planId) return primary
  if (nextDerived.weekStartDate === primaryDerived?.weekStartDate) return primary
  return primary.concat(buildPlannerStripDays(nextDerived))
}

/**
 * Whether dropping/assigning onto ``stripDay`` needs an overwrite confirm.
 * Same-week moves of an already-assigned meal keep silent swap behavior.
 *
 * @param {object|null|undefined} movingMeal
 * @param {object|null|undefined} stripDay
 * @param {string|null|undefined} viewedWeekStart
 */
export function mealMoveNeedsConflictConfirm(movingMeal, stripDay, viewedWeekStart = null) {
  if (!movingMeal || !stripDay?.planItemId || stripDay.isPast) return false
  const targetOccupied = Boolean(
    stripDay.meal?.recipeId || isNightOffMeal(stripDay.meal) || stripDay.status === 'night_off',
  )
  if (!targetOccupied) return false
  if (
    movingMeal.planItemId
    && String(stripDay.planItemId) === String(movingMeal.planItemId)
  ) {
    return false
  }
  if (movingMeal.id && stripDay.meal?.id === movingMeal.id) return false
  const sameWeek = Boolean(
    viewedWeekStart && stripDay.weekStartDate === viewedWeekStart,
  )
  // Same-week reassignment already swaps without prompting.
  if (sameWeek && movingMeal.assignedTo) return false
  return true
}

/**
 * True when a derived plan week has previously generated a grocery list.
 * @param {object|null|undefined} derived
 */
export function weekHasGeneratedGrocery(derived) {
  const meta = derived?.planMetadata
  if (!meta || typeof meta !== 'object') return false
  return Boolean(meta.grocery_list_generated_at || meta.grocery_list_id)
}

/**
 * Group strip-day descriptors by week for the mobile move/assign sheet.
 * @param {Array<object>} stripDays
 * @returns {Array<{ weekStartDate: string, label: string, days: Array<object> }>}
 */
export function groupStripDaysByWeek(stripDays) {
  const current = currentWeekStartIso()
  const next = addWeeksToMonday(current, 1)
  const groups = []
  const byWeek = new Map()

  for (const day of stripDays || []) {
    if (!day?.weekStartDate) continue
    if (!byWeek.has(day.weekStartDate)) {
      byWeek.set(day.weekStartDate, [])
      groups.push(day.weekStartDate)
    }
    byWeek.get(day.weekStartDate).push(day)
  }

  return groups.map((weekStartDate) => {
    let label
    if (weekStartDate === current) label = 'This week'
    else if (weekStartDate === next) label = 'Next week'
    else label = formatWeekLabelFromMonday(weekStartDate)
    return {
      weekStartDate,
      label,
      days: byWeek.get(weekStartDate) || [],
    }
  })
}

/**
 * Fallback Mon–Sun strip labels when live `calendarContext` is missing (should not happen in production).
 * Uses the user's current local calendar week.
 */
export function defaultPlannerCalendarContext() {
  const monday = mondayOfWeekContaining(new Date())
  const y = monday.getFullYear()
  const mo = String(monday.getMonth() + 1).padStart(2, '0')
  const d = String(monday.getDate()).padStart(2, '0')
  const iso = `${y}-${mo}-${d}`
  return {
    dayDates: buildDayDates(iso),
    todayKey: todayKeyInPlanWeek(iso),
  }
}

export function formatWeekLabelFromMonday(weekStartIso, { includeWeekNumber = true } = {}) {
  const monday = parseLocalDateOnly(weekStartIso)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const fmt = (d) => `${months[d.getMonth()]} ${d.getDate()}`
  const range = `${fmt(monday)} – ${fmt(sunday)}`
  if (!includeWeekNumber) return range
  const isoWeek = (() => {
    const d = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
  })()
  return `Week ${isoWeek} · ${range}`
}

/** Planner recipe accents — avoid red hues so bars don’t read as errors. */
const SWATCHES = [
  'var(--planner-swatch-1)',
  'var(--planner-swatch-2)',
  'var(--planner-swatch-3)',
  'var(--planner-swatch-4)',
  'var(--planner-swatch-5)',
  'var(--planner-swatch-6)',
  'var(--planner-swatch-7)',
  'var(--planner-swatch-8)',
]

export function swatchForId(id) {
  if (!id) return SWATCHES[0]
  const s = String(id)
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return SWATCHES[h % SWATCHES.length]
}

/** Same rules as Cook recipe cards: total_time and/or prep_time + cook_time (see recipeMetadataTime.js). */
export function formatRecipeTimeMinutes(meta) {
  const label = formatTotalTimeLabel(getSavedListTotalMinutes(meta))
  return label ?? '—'
}

/**
 * Normalize a MealPlanItemResponse `missing_other` entry. Backend returns objects
 * `{ name, category }`; tolerate plain strings (legacy / suggestion rows).
 * @param {string|object} entry
 * @returns {{ name: string, category: string }|null}
 */
function normalizeMissingEntry(entry) {
  if (!entry) return null
  if (typeof entry === 'string') {
    const n = entry.trim()
    if (!n) return null
    return { name: n, category: 'staples' }
  }
  if (typeof entry === 'object') {
    const name = typeof entry.name === 'string' ? entry.name.trim() : ''
    if (!name) return null
    const cat = typeof entry.category === 'string' && entry.category
      ? entry.category
      : 'staples'
    return { name, category: cat }
  }
  return null
}

/**
 * Missing ingredient count for one planner meal.
 * Uses pantry match totals (need − have); `missing[]` is capped at 8 for display only.
 * @param {{ have?: number, need?: number, missing?: unknown[] }} meal
 */
export function plannerMissingIngredientCount(meal) {
  if (!meal || typeof meal !== 'object') return 0
  const need = typeof meal.need === 'number' ? meal.need : null
  const have = typeof meal.have === 'number' ? meal.have : null
  if (need != null && have != null) {
    return Math.max(0, need - Math.min(Math.max(have, 0), need))
  }
  return Array.isArray(meal.missing) ? meal.missing.length : 0
}

/** Sum {@link plannerMissingIngredientCount} across assigned planner meals. */
export function plannerTotalMissingCount(assigned) {
  const meals = Array.isArray(assigned) ? assigned : []
  return meals.reduce((sum, m) => (
    isNightOffMeal(m) ? sum : sum + plannerMissingIngredientCount(m)
  ), 0)
}

/** Past planner slots should not contribute to grocery shopping needs. */
export function mealContributesToPlannerGrocery(meal) {
  if (!meal || meal.isPast) return false
  if (isNightOffMeal(meal) || !meal.recipeId) return false
  if (meal.status === 'skipped') return false
  return true
}

/** Assigned meals that should contribute to the planner grocery rail / CTA. */
export function plannerGroceryMeals(assigned) {
  return (Array.isArray(assigned) ? assigned : []).filter(mealContributesToPlannerGrocery)
}

/**
 * Stable signature of recipe IDs that contribute to Plan→Shop grocery needs.
 * Day-only rearrangements of the same meals yield the same signature.
 * @param {unknown} assigned
 */
export function plannerGroceryRecipeSignature(assigned) {
  return plannerGroceryMeals(assigned)
    .map((m) => String(m?.recipeId || '').trim())
    .filter(Boolean)
    .sort()
    .join('\0')
}

/** Older API stored this on manual swaps; `is_user_override` is the real flag. */
function planItemReasonForDisplay(reason) {
  if (reason == null || reason === '') return ''
  const t = String(reason).trim()
  if (t.toLowerCase() === 'user override') return ''
  return t
}

/**
 * @param {object} item — MealPlanItem from API
 * @returns {object} meal card for planner
 */
export function planItemToAssignedMeal(item, weekStartIso = null) {
  const key = dayKeyFromDow(item.day_of_week)
  const tc = typeof item.total_count === 'number' ? item.total_count : null
  const mc = typeof item.matched_count === 'number' ? item.matched_count : null
  const need = tc != null ? Math.max(1, tc) : 1
  const have = mc != null ? Math.min(Math.max(mc, 0), need) : 0
  const missing = Array.isArray(item.missing_other)
    ? item.missing_other.map(normalizeMissingEntry).filter(Boolean).slice(0, 8)
    : []
  const missingTotal = Math.max(0, need - have)
  const slotDate = normalizeSlotDateIso(item.date)
    ?? (weekStartIso ? formatLocalDateIso(slotDateForDayKey(weekStartIso, key)) : null)
  const status = typeof item.status === 'string' ? item.status : 'planned'
  const nightOffType = typeof item.night_off_type === 'string' ? item.night_off_type : null
  const isPast = slotDate
    ? isPastSlotDate(slotDate)
    : (weekStartIso ? isPastDayKey(weekStartIso, key) : false)
  const isNightOff = status === 'night_off'
  const aging = item.aging_item && typeof item.aging_item === 'object'
    ? item.aging_item
    : null
  const agingName = typeof aging?.name === 'string' ? aging.name.trim() : ''
  return {
    id: String(item.id),
    planItemId: String(item.id),
    recipeId: item.recipe_id ? String(item.recipe_id) : null,
    name: isNightOff
      ? nightOffLabel(nightOffType)
      : (item.recipe_name || 'Meal'),
    shortLabel: isNightOff ? nightOffLabel(nightOffType) : (item.short_label || item.list_metadata?.short_label || null),
    swatch: swatchForId(item.recipe_id || item.id),
    time: '—',
    have,
    need,
    missingTotal,
    why: planItemReasonForDisplay(item.reason),
    missing,
    assignedTo: key,
    weekStartDate: weekStartIso || null,
    source: 'planned',
    bucket: item.bucket || null,
    slotDate,
    status,
    nightOffType,
    isPast,
    agingItem: aging && aging.id && agingName
      ? {
        id: String(aging.id),
        name: agingName,
        daysAgo: Number.isFinite(Number(aging.days_ago)) ? Number(aging.days_ago) : 0,
      }
      : null,
  }
}

/**
 * @param {object} list — saved list summary from /saved-lists
 */
export function savedListToSuggestionMeal(list) {
  const meta = list.list_metadata || {}
  return {
    id: `recipe-${list.id}`,
    planItemId: null,
    recipeId: String(list.id),
    name: list.name || 'Recipe',
    shortLabel: meta.short_label || null,
    swatch: swatchForId(list.id),
    time: formatRecipeTimeMinutes(meta),
    have: 0,
    need: 1,
    why: '',
    missing: [],
    assignedTo: null,
    source: 'library',
  }
}

/**
 * Planner why-line display parts.
 * We intentionally hide the legacy "Cook Now" label in planner suggestions.
 * Strip it from the beginning of `why` even when bucket metadata is missing.
 * Also hide "From your library" — the row already shows a "From Recipes" source label.
 * Hide diversity *penalty* copy (repeated cuisine/protein) — ranking still demotes those
 * recipes, but the why-line should not advertise the repeat.
 * @param {object} meal — planner meal (`pantryBucket` from suggestions API or `bucket` from plan items)
 */
export function plannerWhyDisplayParts(meal) {
  const raw = typeof meal?.why === 'string' ? meal.why.trim() : ''
  const parts = raw
    .split(/\s*;\s*/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !/^Cook Now$/i.test(p))
    .filter((p) => !/^From your library$/i.test(p))
    .filter((p) => !/^Cuisine already planned this week$/i.test(p))
    .filter((p) => !/^Variety:\s*already planning\b/i.test(p))
  return { showCookNowBadge: false, tail: parts.join('; ') }
}

/**
 * Legacy helper retained for compatibility; planner no longer shows COOK NOW badges.
 */
export function plannerCookNowBadgeVisible() {
  return false
}

/** Collect recipe ids assigned to dinner slots on a meal plan. */
export function recipeIdsFromPlanItems(items) {
  const ids = new Set()
  for (const item of items || []) {
    if (item?.recipe_id != null) ids.add(String(item.recipe_id))
  }
  return ids
}

/**
 * Which plan week a recipe is scheduled on (current takes priority over next).
 * @returns {'current'|'next'|null}
 */
export function planWeekKindForRecipe(recipeId, { currentIds, nextIds } = {}) {
  const id = recipeId != null ? String(recipeId) : ''
  if (!id) return null
  if (currentIds?.has(id)) return 'current'
  if (nextIds?.has(id)) return 'next'
  return null
}

/**
 * Browse / detail badge copy for plan membership.
 * @param {'current'|'next'|null|undefined} weekKind
 * @param {{ inPicks?: boolean }} [opts]
 * @returns {string|null}
 */
export function planAssignmentChipLabel(weekKind, { inPicks = false } = {}) {
  if (weekKind === 'current') return "✓ On this week's plan"
  if (weekKind === 'next') return "✓ On next week's plan"
  if (inPicks) return "✓ In this week's picks"
  return null
}

/**
 * Whether a saved recipe has enough step-by-step directions for Cook Mode.
 * @param {object|null|undefined} list — saved list summary or full list
 */
export function cookReadyFromSavedList(list) {
  if (!list || typeof list !== 'object') return false
  return getRecipeCookReadiness({
    listMetadata: list.list_metadata || {},
    items: Array.isArray(list.items) ? list.items : [],
  }).canCook
}

/**
 * True when an assigned plan meal on today can launch Cook Mode.
 * @param {object|null|undefined} meal
 * @param {string|null|undefined} todayKey — MON…SUN key for today in the plan week
 */
export function plannerAssignedMealCanStartCooking(meal, todayKey) {
  if (!meal?.recipeId || isNightOffMeal(meal)) return false
  if (meal.isPast || meal.status !== 'planned') return false
  if (todayKey == null || meal.assignedTo !== todayKey) return false
  return meal.canCook === true
}

/**
 * Map GET /meal-plans/{id}/suggestions row → planner meal card
 * @param {object} row — PlannerSuggestionItem from API
 */
export function suggestionApiRowToMeal(row) {
  const need = Math.max(1, row.total_count || 1)
  const have = Math.min(row.matched_count ?? 0, need)
  const missingTotal = Math.max(0, need - have)
  const meta = row.total_time ? { total_time: row.total_time } : {}
  const src = row.source
  const source = src === 'pantry' || src === 'candidate' ? 'match' : 'library'
  const missing = Array.isArray(row.missing_other)
    ? row.missing_other.map(normalizeMissingEntry).filter(Boolean)
    : []
  return {
    id: `sugg-${row.recipe_id}`,
    planItemId: null,
    recipeId: String(row.recipe_id),
    name: row.name || 'Recipe',
    shortLabel: row.short_label || null,
    swatch: swatchForId(row.recipe_id),
    time: formatRecipeTimeMinutes(meta),
    have,
    need,
    missingTotal,
    why: row.why || '',
    missing,
    assignedTo: null,
    source,
    suggestionSource: src,
    pantryBucket: row.bucket ?? null,
  }
}

/**
 * Split planner suggestion meals: user-added (Cook → "Add to Meal Plan") vs ranked picks.
 * Both groups preserve API order so backend diversity ranking (protein/cuisine/pantry) is kept.
 * @param {object[]|null|undefined} meals
 * @returns {{ userPicks: object[], recommended: object[] }}
 */
export function partitionPlannerSuggestions(meals) {
  const list = Array.isArray(meals) ? meals : []
  const userPicks = []
  const recommended = []
  for (const m of list) {
    if (m && m.suggestionSource === 'candidate') userPicks.push(m)
    else if (m) recommended.push(m)
  }
  return { userPicks, recommended }
}

/**
 * True when this person has any meal-preference fields we surface in planner copy.
 * @param {object|null|undefined} d
 */
export function mealPrefsDietaryHasContent(d) {
  if (!d || typeof d !== 'object') return false
  if (d.diet && String(d.diet).trim()) return true
  if (d.vegetarian || d.vegan || d.gluten_free) return true
  const allergies = Array.isArray(d.allergies) ? d.allergies.filter((x) => x && String(x).trim()) : []
  if (allergies.length > 0) return true
  const dislikes = Array.isArray(d.dislikes) ? d.dislikes.filter((x) => x && String(x).trim()) : []
  if (dislikes.length > 0) return true
  return false
}

/** @param {string[]} names */
export function formatPreferenceNamesList(names) {
  const n = names.filter(Boolean).map((s) => String(s).trim()).filter(Boolean)
  if (n.length === 0) return ''
  if (n.length === 1) return n[0]
  if (n.length === 2) return `${n[0]} and ${n[1]}`
  return `${n.slice(0, -1).join(', ')}, and ${n[n.length - 1]}`
}

/**
 * Whose saved meal preferences inform suggestion ranking.
 * @param {object|null|undefined} prefs — full preferences payload
 * @param {string} accountDisplayName — first name or email local-part
 * @returns {string[]}
 */
export function buildPreferencePeopleNames(prefs, accountDisplayName) {
  if (!prefs || typeof prefs !== 'object') return []
  const names = []
  const youDietary = prefs.dietary ?? {}
  if (mealPrefsDietaryHasContent(youDietary)) {
    names.push(accountDisplayName || 'You')
  }
  const members = prefs.family_members ?? {}
  Object.entries(members).forEach(([name, data]) => {
    if (mealPrefsDietaryHasContent(data?.dietary)) names.push(String(name).trim())
  })
  return names.filter(Boolean)
}

/**
 * List-level explanation of planner suggestion ranking (not per-recipe why copy).
 * Matches GET /meal-plans/{id}/suggestions: pantry score first, then saved
 * preferences (when present) and protein/cuisine variety.
 * @param {object[]|null|undefined} recommended
 * @param {{ dietary?: object|null, accountDisplayName?: string }} [opts]
 * @returns {string}
 */
export function buildRecommendationWhySummary(recommended, opts = {}) {
  if (!Array.isArray(recommended) || recommended.length === 0) return ''
  const { dietary = null, accountDisplayName = 'You' } = opts
  const people = buildPreferencePeopleNames(dietary, accountDisplayName)
  const prefsPhrase = people.length > 0
    ? ` We then apply saved meal preferences for ${formatPreferenceNamesList(people)}, and mix proteins and cuisines so the week stays varied.`
    : ' We then mix proteins and cuisines so the week stays varied.'
  return (
    'These recipes are ranked by pantry match first: how many ingredients you already have.' +
    prefsPhrase
  )
}

/**
 * Build slotsByDay + assigned + suggestions from API plan + recipe summaries.
 * @param {object} plan — MealPlanResponse
 * @param {object[]} recipeLists — saved list summaries (recipes only)
 * @param {{ ok: true, rows: object[] } | { ok: false, rows: null } | null} apiSuggestions —
 *   ranked rows from GET /meal-plans/{id}/suggestions. When `ok` is false or `rows`
 *   is missing, suggestions stay empty — we do not dump the entire recipe library
 *   (that produced misleading counts and looked like "11 random fill" cards).
 */
export function deriveLivePlannerState(plan, recipeLists, apiSuggestions = null) {
  const items = [...(plan.items || [])].sort((a, b) => a.day_of_week - b.day_of_week)
  const slotsByDay = {}
  MEAL_PLAN_DAY_ORDER.forEach((k) => {
    slotsByDay[k] = { planItemId: null, recipeId: null }
  })
  const usedRecipeIds = new Set()
  const assigned = []

  const weekStart = plan.week_start_date
  for (const item of items) {
    const key = dayKeyFromDow(item.day_of_week)
    const slotDate = normalizeSlotDateIso(item.date)
    slotsByDay[key] = {
      planItemId: String(item.id),
      recipeId: item.recipe_id ? String(item.recipe_id) : null,
      status: typeof item.status === 'string' ? item.status : 'planned',
      nightOffType: typeof item.night_off_type === 'string' ? item.night_off_type : null,
      slotDate,
      isPast: slotDate
        ? isPastSlotDate(slotDate)
        : isPastDayKey(weekStart, key),
    }
    if (planItemIsAssigned(item)) {
      if (item.recipe_id) usedRecipeIds.add(String(item.recipe_id))
      assigned.push(planItemToAssignedMeal(item, weekStart))
    }
  }

  assigned.sort((a, b) => dowFromDayKey(a.assignedTo) - dowFromDayKey(b.assignedTo))

  let suggestions = []
  if (apiSuggestions?.ok === true) {
    const used = usedRecipeIds
    suggestions = (apiSuggestions.rows || [])
      .filter((r) => r && r.recipe_id && !used.has(String(r.recipe_id)))
      .map((r) => suggestionApiRowToMeal(r))
  }

  return {
    planId: String(plan.id),
    weekStartDate: plan.week_start_date,
    generatedAt: plan.generated_at || null,
    status: typeof plan.status === 'string' ? plan.status : 'draft',
    planMetadata:
      plan.plan_metadata && typeof plan.plan_metadata === 'object'
        ? { ...plan.plan_metadata }
        : {},
    slotsByDay,
    assigned,
    suggestions,
  }
}

/**
 * Merge API meal-plan item payloads into derived planner state (optimistic updates).
 * @param {object} derived — from {@link deriveLivePlannerState}
 * @param {object[]} items — MealPlanItem API rows from PUT/PATCH
 */
export function patchDerivedFromPlanItems(derived, items) {
  if (!derived?.weekStartDate || !Array.isArray(items) || items.length === 0) {
    return derived
  }
  const weekStart = derived.weekStartDate
  const next = {
    ...derived,
    slotsByDay: { ...derived.slotsByDay },
    assigned: [...derived.assigned],
    suggestions: [...derived.suggestions],
  }
  const assignedRecipeIds = new Set()

  for (const item of items) {
    const key = dayKeyFromDow(item.day_of_week)
    const planItemId = String(item.id)
    const recipeId = item.recipe_id ? String(item.recipe_id) : null
    const slotDate = normalizeSlotDateIso(item.date)
    const status = typeof item.status === 'string' ? item.status : 'planned'
    const isPast = slotDate ? isPastSlotDate(slotDate) : isPastDayKey(weekStart, key)

    next.slotsByDay[key] = {
      planItemId,
      recipeId,
      status,
      nightOffType: typeof item.night_off_type === 'string' ? item.night_off_type : null,
      slotDate,
      isPast,
    }
    next.assigned = next.assigned.filter((m) => m.planItemId !== planItemId)
    if (planItemIsAssigned(item)) {
      next.assigned.push(planItemToAssignedMeal(item, weekStart))
      if (recipeId) assignedRecipeIds.add(recipeId)
    }
  }

  if (assignedRecipeIds.size > 0) {
    next.suggestions = next.suggestions.filter(
      (m) => !m.recipeId || !assignedRecipeIds.has(String(m.recipeId)),
    )
  }

  next.assigned.sort(
    (a, b) => dowFromDayKey(a.assignedTo) - dowFromDayKey(b.assignedTo),
  )
  return next
}

/**
 * Replace suggestion rail from API suggestion rows (e.g. after pin/unpin candidate).
 * @param {object} derived
 * @param {object[]} suggestionRows — PlannerSuggestionItem-shaped API rows
 * @param {object} plan — MealPlanResponse used to exclude already-assigned recipes
 */
export function patchDerivedSuggestionsFromApi(derived, suggestionRows, plan) {
  if (!derived || !plan) return derived
  const used = new Set(
    (plan.items || [])
      .filter((it) => it?.recipe_id)
      .map((it) => String(it.recipe_id)),
  )
  const apiSuggestions = {
    ok: true,
    rows: (suggestionRows || []).filter(
      (r) => r?.recipe_id && !used.has(String(r.recipe_id)),
    ),
  }
  const suggestions = deriveLivePlannerState(plan, [], apiSuggestions).suggestions
  return { ...derived, suggestions }
}
